import {
  TaxonomyConstant,
  DEFAULT_TASK_BUILDER_FEATURES_FOR_NEW_PROJECTS,
} from '@scale/shared/mist/types';
import { TextCollectionFieldType } from '@scale/shared/types/params/textcollection';
import { InteractionStepType, MultiTurnInteractionConfigurationV2 } from '@scale/shared/types/chat';
import { ThoughtStepType } from '@scale/shared/types/chatlite';
import {
  StepsChunkingStrategyEnum,
  CHUNK_BY_DELIMITER_DEFAULT_DELIMITER,
  CHUNK_BY_DELIMITER_DEFAULT_USER_PROMPT,
  DEFAULT_STEPS_REGEN_TEMPLATE,
} from '@scale/shared/processSupervision/types';
import {
  TASK_STANDARDIZED_QUALITY_MEASUREMENT_STEP,
  QualityMeasurementType,
} from '@scale/shared/types/chat/qms';

export const BLANK_TASK_PARAMS_TEMPLATE: MultiTurnInteractionConfigurationV2 = {
  minTurns: 1,
  maxTurns: 1,
  history: {},
  before: [],
  after: [],
  turn: [
    {
      id: TaxonomyConstant.Eval.StandardStepId.PerResponseRatings,
      type: InteractionStepType.TextCollectionPerResponse,
      title: 'Loading your task template…',
      subtitle: 'Loading your task template',
      params: {
        fieldToCheck: TaxonomyConstant.Eval.StandardFieldId.OverallScore,
        visibility_restricted: [-1, 0, 10],
        fields: [
          {
            required: true,
            min: 1,
            max: 5,
            step: 1,
            use_slider: true,
            type: TextCollectionFieldType.Number,
            field_id: TaxonomyConstant.Eval.StandardFieldId.Accuracy,
            title: 'A default task template is loading…',
            description: 'A default task template is loading…',
            prefix: 'Insufficient',
            mid_label: 'Minimum',
            suffix: 'Good',
            rubric: 'A default task template is loading…',
          },
        ],
      },
    },
  ],
  taskBuilderFeatures: DEFAULT_TASK_BUILDER_FEATURES_FOR_NEW_PROJECTS,
} as any;

export const EVAL_TASK_PARAMS_TEMPLATE: MultiTurnInteractionConfigurationV2 = {
  minTurns: 1,
  maxTurns: 1,
  history: {
    prevHistory: '',
    lastPrompt: '',
    modelResponses: [
      {
        id: 'response_one',
        content:
          'Molly had always been fascinated by the history, culture, and romance of Europe. As a 21-year-old college student, she had spent countless hours pouring over travel blogs, Instagram accounts, and guidebooks, dreaming of the day she could finally explore the continent herself.\n\nFinally, after months of saving and planning, the day arrived.Molly packed her bags, said goodbye to her friends and family, and boarded a flight to London, the first stop on her three- week European adventure.',
      },
      {
        id: 'response_two',
        content:
          'Once upon a time, in a small town nestled in the heart of America, lived a young woman named Molly. At 21, full of dreams and a wanderlust spirit, Molly longed to explore the world beyond the familiar. Her heart was set on Europe, a continent brimming with history, culture, and adventures waiting to be discovered.',
      },
    ],
  },
  before: [
    // WARNING: before deleting this step from the template
    // Make sure to put in TaxonomyConstant.Eval.PlaceholderStepId.PromptInputPlaceholderForEmptyInitialQuestions step
    // vvvvvvv START WARNING SECTION vvvvvvv
    {
      id: TaxonomyConstant.Eval.StandardStepId.Initial,
      type: InteractionStepType.PromptTextCollection,
      title: 'Please Read the Conversation History',
      subtitle:
        'Carefully read the conversation history and then the latest prompt. After this, you will be given two model responses that answer the latest prompt. Remember to use the conversation history as context while evaluating these responses.',
      params: {
        editable_during_consensus: true,
        fields: [
          {
            required: true,
            type: 'category',
            field_id: TaxonomyConstant.Eval.StandardFieldId.RejectReason,
            title: 'Grammar Issues',
            description: 'Does the prompt have any grammar issues?',
            choices: [
              {
                label: 'Yes',
                value: 'Yes',
              },
              {
                label: 'No',
                value: 'No',
              },
            ],
          },
        ],
      },
    },
    // ^^^^^^ END WARNING SECTION ^^^^^^
  ],
  turn: [
    {
      id: TaxonomyConstant.Eval.StandardStepId.PerResponseRatings,
      type: InteractionStepType.TextCollectionPerResponse,
      title: 'Response Rating',
      subtitle:
        'Rate the response based on each criteria that is given below based on the given prompt.',
      params: {
        fieldToCheck: TaxonomyConstant.Eval.StandardFieldId.OverallScore,
        editable_during_consensus: true,
        fields: [
          {
            required: true,
            min: 1,
            max: 5,
            step: 1,
            use_slider: true,
            type: TextCollectionFieldType.Number,
            field_id: TaxonomyConstant.Eval.StandardFieldId.Accuracy,
            title: 'Accuracy',
            description:
              'The extent to which the information presented is accurate, reliable, and aligns with established facts or evidence.',
            prefix: 'Insufficient',
            mid_label: 'Minimum',
            suffix: 'Good',
            rubric:
              '| Criteria                                                                                                                                                       | 1-2  (Insufficient)                                                                                                                                                                                                                                                                                                                                                         | 3 (Minimum)                                                                                                                                                                                                                                                                                                                                                         | 4-5 (Good)                                                                                                                                                                                                                                                                                                                                                                                                                                          |\n| :------------------------------------------------------------------------------------------------------------------------------------------------------------- | :-------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------- | :------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------ | :-------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------- |\n| <h3>Accuracy</h3>*The extent to which the information presented is accurate, reliable, and aligns with established facts or evidence.*<br><h3>Weight: 25%</h3> | <ul><li><b>Central Claims</b> - One or more incorrect claims are central or core to the conclusion, thesis, or key supporting points of the response</li><br><li><b>Supporting Claims</b> - 2 or more pieces of supporting evidence are false</li><br><li><b>Verifiable</b> - Conclusive, common-knowledge counterfactual information can be found via a Google search</li> | <ul><li><b>Central Claims</b> - All central claims are correct and accurate</li><br><li><b>Supporting Claims</b> - Up to 1 incorrect supporting point that, if corrected, would not meaningfully alter the core thesis or conclusion</li><br><li><b>Verifiable</b> - Falsely asserts claims or evidence that are not fully proven / controversial as fact</li></ul> | <ul><li><b>Central Claims</b> - All central claims are correct and accurate</li><br><li><b>Supporting claims</b> - All supporting claims are accurate and correct</li><br><li><b>Verifiable</b> - No claims or evidence are incorrect or false, and are verifiable through general available information, and trusted sources. All potentially controversial,  generalized statements or opinions  are presented with appropriate caveats</li></ul> |\n\n',
          },
          {
            required: true,
            min: 1,
            max: 5,
            step: 1,
            use_slider: true,
            type: TextCollectionFieldType.Number,
            field_id: TaxonomyConstant.Eval.StandardFieldId.InstructionFollowing,
            title: 'Instruction Following',
            description:
              'The extent to which the answer addresses all aspects of the prompt, ensuring that no essential information is omitted.',
            prefix: 'Insufficient',
            mid_label: 'Minimum',
            suffix: 'Good',
            rubric:
              '| Criteria                                                                                                                                                                                      | 1-2  (Insufficient)                                                                                                                                                                                                                   | 3 (Minimum)                                                                                                                                                                                                                                                      | 4-5 (Good)                                                                                                                                                                                                                                                    |\n| :-------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------- | :------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------ | :--------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------- | :------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------ |\n| <h3>Completeness / Instruction-Following</h3>*The extent to which the answer addresses all aspects of the prompt, ensuring that no essential information is omitted.*<br><h3>Weight: 15%</h3> | <ul><li><b>Prompt Request Coverage</b> - The response does not address all explicit asks of the prompt</li><br><li><b>Constraints</b> - The prompt did not address all of the constraints placed in service of the requests</li></ul> | <ul><li><b>Prompt Request Coverage</b> -The response addresses all explicit asks of the prompt</li><br><li><b>Constraints</b> - <br>The prompt completed the requests within the prompt, including the constraints placed in service of those requests</li></ul> | <ul><li><b>Prompt Request Coverage</b> - The response addresses all explicit asks of the prompt</li><br><li><b>Constraints</b> - The prompt completed the requests within the prompt, including the constraints placed in service of those requests</li></ul> |\n\n',
          },
          {
            required: true,
            min: 1,
            max: 5,
            step: 1,
            use_slider: true,
            type: TextCollectionFieldType.Number,
            field_id: TaxonomyConstant.Eval.StandardFieldId.Grammar,
            title: 'Grammar & Presentation',
            description:
              'The distinctive method in which ideas are expressed through writing focusing primarily on the stylistic, mechanical and syntactical components.',
            prefix: 'Insufficient',
            mid_label: 'Minimum',
            suffix: 'Good',
            rubric:
              '| Criteria                                                                                                                                                                                      | 1-2  (Insufficient)                                                                                                                                                                                                                   | 3 (Minimum)                                                                                                                                                                                                                                                      | 4-5 (Good)                                                                                                                                                                                                                                                    |\n| :-------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------- | :------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------ | :--------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------- | :------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------ |\n| <h3>Completeness / Instruction-Following</h3>*The extent to which the answer addresses all aspects of the prompt, ensuring that no essential information is omitted.*<br><h3>Weight: 15%</h3> | <ul><li><b>Prompt Request Coverage</b> - The response does not address all explicit asks of the prompt</li><br><li><b>Constraints</b> - The prompt did not address all of the constraints placed in service of the requests</li></ul> | <ul><li><b>Prompt Request Coverage</b> -The response addresses all explicit asks of the prompt</li><br><li><b>Constraints</b> - <br>The prompt completed the requests within the prompt, including the constraints placed in service of those requests</li></ul> | <ul><li><b>Prompt Request Coverage</b> - The response addresses all explicit asks of the prompt</li><br><li><b>Constraints</b> - The prompt completed the requests within the prompt, including the constraints placed in service of those requests</li></ul> |\n\n',
          },
          {
            required: true,
            min: 1,
            max: 5,
            step: 1,
            use_slider: true,
            type: TextCollectionFieldType.Number,
            field_id: TaxonomyConstant.Eval.StandardFieldId.Relevance,
            title: 'Relevance',
            description:
              'How useful the supporting information and claims are in answering the question or prompt.',
            prefix: 'Insufficient',
            mid_label: 'Minimum',
            suffix: 'Good',
            rubric:
              '| Criteria                                                                                                                                                                                      | 1-2  (Insufficient)                                                                                                                                                                                                                   | 3 (Minimum)                                                                                                                                                                                                                                                      | 4-5 (Good)                                                                                                                                                                                                                                                    |\n| :-------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------- | :------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------ | :--------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------- | :------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------ |\n| <h3>Completeness / Instruction-Following</h3>*The extent to which the answer addresses all aspects of the prompt, ensuring that no essential information is omitted.*<br><h3>Weight: 15%</h3> | <ul><li><b>Prompt Request Coverage</b> - The response does not address all explicit asks of the prompt</li><br><li><b>Constraints</b> - The prompt did not address all of the constraints placed in service of the requests</li></ul> | <ul><li><b>Prompt Request Coverage</b> -The response addresses all explicit asks of the prompt</li><br><li><b>Constraints</b> - <br>The prompt completed the requests within the prompt, including the constraints placed in service of those requests</li></ul> | <ul><li><b>Prompt Request Coverage</b> - The response addresses all explicit asks of the prompt</li><br><li><b>Constraints</b> - The prompt completed the requests within the prompt, including the constraints placed in service of those requests</li></ul> |\n\n',
          },
          {
            required: true,
            min: 1,
            max: 5,
            step: 1,
            use_slider: true,
            type: TextCollectionFieldType.Number,
            field_id: TaxonomyConstant.Eval.StandardFieldId.Verbosity,
            title: 'Verbosity',
            description:
              'The response does not convey unnecessary or superfluous information. It is as “snappy” as it can be, while still fully and comprehensively answering every component of the prompt.',
            prefix: 'Very verbose',
            mid_label: 'Somewhat verbose',
            suffix: 'Not verbose',
            rubric:
              '| Criteria                                                                                                                                                                                      | 1-2  (Insufficient)                                                                                                                                                                                                                   | 3 (Minimum)                                                                                                                                                                                                                                                      | 4-5 (Good)                                                                                                                                                                                                                                                    |\n| :-------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------- | :------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------ | :--------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------- | :------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------ |\n| <h3>Completeness / Instruction-Following</h3>*The extent to which the answer addresses all aspects of the prompt, ensuring that no essential information is omitted.*<br><h3>Weight: 15%</h3> | <ul><li><b>Prompt Request Coverage</b> - The response does not address all explicit asks of the prompt</li><br><li><b>Constraints</b> - The prompt did not address all of the constraints placed in service of the requests</li></ul> | <ul><li><b>Prompt Request Coverage</b> -The response addresses all explicit asks of the prompt</li><br><li><b>Constraints</b> - <br>The prompt completed the requests within the prompt, including the constraints placed in service of those requests</li></ul> | <ul><li><b>Prompt Request Coverage</b> - The response addresses all explicit asks of the prompt</li><br><li><b>Constraints</b> - The prompt completed the requests within the prompt, including the constraints placed in service of those requests</li></ul> |\n\n',
          },
          {
            required: true,
            min: 1,
            max: 5,
            step: 1,
            use_slider: true,
            type: TextCollectionFieldType.Number,
            field_id: TaxonomyConstant.Eval.StandardFieldId.Depth,
            title: 'Depth',
            description: 'The level of detail, insight, and nuance the response provides.',
            prefix: 'Insufficient',
            mid_label: 'Minimum',
            suffix: 'Good',
            rubric:
              '| Criteria                                                                                                                                                                                      | 1-2  (Insufficient)                                                                                                                                                                                                                   | 3 (Minimum)                                                                                                                                                                                                                                                      | 4-5 (Good)                                                                                                                                                                                                                                                    |\n| :-------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------- | :------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------ | :--------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------- | :------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------ |\n| <h3>Completeness / Instruction-Following</h3>*The extent to which the answer addresses all aspects of the prompt, ensuring that no essential information is omitted.*<br><h3>Weight: 15%</h3> | <ul><li><b>Prompt Request Coverage</b> - The response does not address all explicit asks of the prompt</li><br><li><b>Constraints</b> - The prompt did not address all of the constraints placed in service of the requests</li></ul> | <ul><li><b>Prompt Request Coverage</b> -The response addresses all explicit asks of the prompt</li><br><li><b>Constraints</b> - <br>The prompt completed the requests within the prompt, including the constraints placed in service of those requests</li></ul> | <ul><li><b>Prompt Request Coverage</b> - The response addresses all explicit asks of the prompt</li><br><li><b>Constraints</b> - The prompt completed the requests within the prompt, including the constraints placed in service of those requests</li></ul> |\n\n',
          },
          {
            min: 1,
            max: 5,
            step: 1,
            required: true,
            use_slider: true,
            type: TextCollectionFieldType.Number,
            field_id: TaxonomyConstant.Eval.StandardFieldId.OverallScore,
            title: 'Overall Score',
            description:
              'The overall score should be determined by taking into account these response characteristics: accuracy, instructions following, safety, helpfulness, and formatting/writing style. Think about the overall usefulness of the response before choosing a score. Do not only consider accuracy and instruction following.',
            prefix: 'Horrible',
            mid_label: 'Okay',
            suffix: 'Amazing',
          },
        ],
        // conditional_visibility: {
        //   type: 'AND',
        //   params: [
        //     {
        //       id: TaxonomyConstant.Eval.StandardStepId.Initial,
        //       type: InteractionStepType.PromptTextCollection,
        //       params: {
        //         fields: {
        //           type: 'AND',
        //           params: [
        //             {
        //               type: TextCollectionFieldType.Category,
        //               field_id: TaxonomyConstant.Eval.StandardFieldId.RejectReason,
        //               operator: 'eq',
        //               value: 'No',
        //             },
        //           ],
        //         },
        //       },
        //     },
        //   ],
        // },
      },
    },
    {
      id: TaxonomyConstant.Eval.StandardStepId.SideBySide,
      type: InteractionStepType.QuantitativeModelResponseSelector,
      title: 'Answer the questions below',
      subtitle: 'Label your response according to the questions below.',
      params: {
        editable_during_consensus: true,
        // conditional_visibility: {
        //   type: 'AND',
        //   params: [
        //     {
        //       id: TaxonomyConstant.Eval.StandardStepId.Initial,
        //       type: InteractionStepType.PromptTextCollection,
        //       params: {
        //         fields: {
        //           type: 'AND',
        //           params: [
        //             {
        //               type: TextCollectionFieldType.Category,
        //               field_id: TaxonomyConstant.Eval.StandardFieldId.RejectReason,
        //               operator: 'eq',
        //               value: 'No',
        //             },
        //           ],
        //         },
        //       },
        //     },
        //   ],
        // },
        min_likert: 1,
        max_likert: 5,
        tiebreak_neutral_likert: true,
        fields: [
          {
            required: true,
            min_characters: 100,
            type: TextCollectionFieldType.Text,
            field_id: TaxonomyConstant.Eval.StandardFieldId.RankingJustification,
            title: 'Ranking Justification',
            description:
              'Write a justification reasoning on why you ranked the way you did above. MAKE SURE NOT TO REFERENCE THE RUBRIC DIRECTLY and do not summarize the response. Be detailed and concrete. Spell out your thought process and explain why one response is better than the other or why you have no preference. Avoid writing in first person.',
          },
          {
            required: true,
            type: TextCollectionFieldType.Boolean,
            field_id: TaxonomyConstant.Eval.StandardFieldId.Objectivity,
            title:
              'Do you think the prompt or responses are objective, and there is definitely a clear better answer between the two responses?',
          },
        ],
      },
    },
  ],
  after: [
    TASK_STANDARDIZED_QUALITY_MEASUREMENT_STEP[QualityMeasurementType.Task],
    {
      id: TaxonomyConstant.Eval.StandardStepId.ReviewerFeedback,
      type: InteractionStepType.TextCollection,
      title: 'Provide feedback to the previous attempter',
      params: {
        editable_during_consensus: true,
        visibility_restricted: [0, 1, 4, 10, 12],
        conditional_visibility: {
          type: 'AND',
          params: [], // vacuously true due to `every` operator iterating over this
        },
        fields: [
          {
            required: true,
            field_id: TaxonomyConstant.Eval.StandardFieldId.ValidRejectReason,
            type: TextCollectionFieldType.Boolean,
            title: 'Was the reject reason for the task correctly determined?',
          },
          {
            required: true,
            field_id: TaxonomyConstant.Eval.StandardFieldId.PreferenceAgreement,
            type: TextCollectionFieldType.Boolean,
            title: 'Did you agree with the preference rank provided by the previous attempter?',
          },
          {
            required: true,
            field_id: TaxonomyConstant.Eval.StandardFieldId.JustificationRating,
            min: 1,
            step: 1,
            max: 5,
            use_slider: true,
            type: TextCollectionFieldType.Number,
            title: 'How good was the justification provided by the previous attempter?',
            prefix: 'Poor Justification ',
            mid_label: 'Okay Justification ',
            suffix: 'Good Justification ',
            description: 'If the task was rejected for any reason, mark this answer as a 5',
          },
          {
            required: false,
            field_id: TaxonomyConstant.Eval.StandardFieldId.Feedback,
            save_to_task_feedback: true,
            type: TextCollectionFieldType.Text,
            title: 'Feedback for the Attempter:',
            description:
              'Please provide actionable feedback to the previous attempter. Take into account the response ratings, preference rank and justification while providing feedback.',
          },
        ],
      },
    },
  ],
  taskBuilderFeatures: DEFAULT_TASK_BUILDER_FEATURES_FOR_NEW_PROJECTS,
} as any;

export const RLHF_TASK_PARAMS_TEMPLATE: MultiTurnInteractionConfigurationV2 = {
  minTurns: 1,
  maxTurns: 1,
  history: [],
  before: [],
  after: [TASK_STANDARDIZED_QUALITY_MEASUREMENT_STEP[QualityMeasurementType.Task]],
  turn: [
    {
      id: TaxonomyConstant.RLHF.StandardStepId.Prompt,
      type: InteractionStepType.PromptInput,
      subtitle:
        'Write a prompt to start a chat session with the model. Ensure that you prompt the model for a response that you can verify yourself, with certainty.',
      title: 'Start a chat',
      params: {
        needsUserInput: true,
        instructions:
          'You can link a reference doc here/specify what you need: <for example, gdoc link>',
      },
    },
    {
      id: TaxonomyConstant.RLHF.StandardStepId.PerResponseRatings,
      type: InteractionStepType.TextCollectionPerResponse,
      title: 'Response Rating',
      subtitle:
        'Rate the response based on each criteria that is given below based on the given prompt.',
      params: {
        fields: [
          {
            required: true,
            min: 1,
            max: 5,
            step: 1,
            use_slider: true,
            type: TextCollectionFieldType.Number,
            field_id: TaxonomyConstant.Eval.StandardFieldId.Accuracy,
            title: 'Accuracy',
            description:
              'The extent to which the information presented is accurate, reliable, and aligns with established facts or evidence.',
            prefix: 'Insufficient',
            mid_label: 'Minimum',
            suffix: 'Good',
            rubric: 'Insert rubric here',
          },
        ],
      },
    },
    {
      id: TaxonomyConstant.RLHF.StandardStepId.SideBySide,
      type: InteractionStepType.QuantitativeModelResponseSelector,
      title: 'Answer the questions below',
      subtitle: 'Label your response according to the questions below.',
      params: {
        chat_models: [
          {
            id: 'model_one',
            url: 'https://graph-genai.facebook.com/v18.0',
            params: {
              stream: true,
              sampleModelParams: {
                workstreamName: 'multilingual-mango',
              },
            },
          },
          {
            id: 'model_two',
            url: 'https://graph-genai.facebook.com/v18.0',
            params: {
              stream: true,
              sampleModelParams: {
                workstreamName: 'multilingual-mango',
              },
            },
          },
        ],

        min_likert: 1,
        max_likert: 5,
        tiebreak_neutral_likert: true,
        fields: [
          {
            required: true,
            min_characters: 100,
            type: TextCollectionFieldType.Text,
            field_id: TaxonomyConstant.RLHF.StandardFieldId.RankingJustification,
            title: 'Ranking Justification',
            description:
              'Write a justification reasoning on why you ranked the way you did above. MAKE SURE NOT TO REFERENCE THE RUBRIC DIRECTLY and do not summarize the response. Be detailed and concrete. Spell out your thought process and explain why one response is better than the other or why you have no preference. Avoid writing in first person.',
          },
          {
            required: true,
            type: TextCollectionFieldType.Category,
            field_id: TaxonomyConstant.RLHF.StandardFieldId.SelectedResponseIsGood,
            title: 'Do you think the selected response is good?',
            choices: [
              {
                label: 'Yes',
                value: 'Yes',
              },
              {
                label: 'No',
                value: 'No',
              },
            ],
          },
        ],
      },
    },
    // {
    //   id: TaxonomyConstant.RLHF.StandardStepId.BetterResponseQuestions,
    //   type: InteractionStepType.ResponseTextCollection,
    //   title: 'Answer the questions below',
    //   subtitle: 'Label your selected response according to the questions below.',
    //   params: {
    //     fields: [
    //       {
    //         required: true,
    //         min_characters: 100,
    //         type: TextCollectionFieldType.Text,
    //         field_id: TaxonomyConstant.RLHF.StandardFieldId.BetterResponseJustification,
    //         title: 'Ranking Justification',
    //         description:
    //           'Write a justification reasoning on why you ranked the way you did above. MAKE SURE NOT TO REFERENCE THE RUBRIC DIRECTLY and do not summarize the response. Be detailed and concrete. Spell out your thought process and explain why one response is better than the other or why you have no preference. Avoid writing in first person.',
    //       },
    //     ],
    //   },
    // },
    {
      id: TaxonomyConstant.RLHF.StandardStepId.ModelResponseEditing,
      type: InteractionStepType.ModelResponseEditor,
      title: 'Edit the model response',
      subtitle:
        'Edit the model response to improve its quality. You can edit the response to make it more coherent, accurate, or engaging.',
      params: {
        conditional_visibility: {
          type: 'AND',
          params: [
            {
              id: TaxonomyConstant.RLHF.StandardStepId.SideBySide,
              type: InteractionStepType.QuantitativeModelResponseSelector,
              params: {
                fields: {
                  type: 'AND',
                  params: [
                    {
                      type: TextCollectionFieldType.Category,
                      field_id: TaxonomyConstant.RLHF.StandardFieldId.SelectedResponseIsGood,
                      operator: 'eq',
                      value: 'Yes',
                    },
                  ],
                },
              },
            },
          ],
        },
      },
    },
  ],
  taskBuilderFeatures: DEFAULT_TASK_BUILDER_FEATURES_FOR_NEW_PROJECTS,
} as any;

export const SFT_TASK_PARAMS_TEMPLATE: MultiTurnInteractionConfigurationV2 = {
  minTurns: 1,
  maxTurns: 1,
  history: [],
  before: [],
  after: [TASK_STANDARDIZED_QUALITY_MEASUREMENT_STEP[QualityMeasurementType.Task]],
  turn: [
    {
      id: TaxonomyConstant.SFT.StandardStepId.Prompt,
      type: InteractionStepType.PromptInput,
      title: 'Start a chat',
      subtitle:
        'Write a prompt to start a chat session with the model. Ensure that you prompt the model for a response that you can verify yourself, with certainty.',
      params: {
        needsUserInput: true,
        instructions:
          'You can link a reference doc here/specify what you need: <for example, gdoc link>',
      },
    },
    // Following comments are placeholders
    // Post prompt questions is optional
    // Initial user response is not supported yet
    // Post response questions depends on initial user response
    {
      id: TaxonomyConstant.SFT.StandardStepId.ModelResponseComparison,
      type: InteractionStepType.ModelResponseSelector,
      title: 'Answer the questions below',
      subtitle: 'Label your response according to the questions below.',
      params: {
        chat_models: [
          {
            id: 'sota_model',
            url: 'https://api.openai.com/v1/chat/completions',
            params: {
              stream: true,
              max_tokens: 1024,
              model: 'gpt-3.5-turbo',
            },
          },
        ],
      },
    },
    {
      id: TaxonomyConstant.SFT.StandardStepId.BetterResponseQuestions,
      type: InteractionStepType.ResponseTextCollection,
      title: 'Answer the questions below',
      subtitle: 'Label your selected response according to the questions below.',
      params: {
        fields: [
          {
            required: true,
            type: TextCollectionFieldType.Category,
            field_id: TaxonomyConstant.SFT.StandardFieldId.SelectedResponseIsGood,
            title: 'Do you think the selected response is good?',
            choices: [
              {
                label: 'Yes',
                value: 'Yes',
              },
              {
                label: 'No',
                value: 'No',
              },
            ],
          },
        ],
      },
    },
    {
      id: TaxonomyConstant.SFT.StandardStepId.PerResponseRatings,
      type: InteractionStepType.TextCollectionPerResponse,
      title: 'Response Rating',
      subtitle:
        'Rate the response based on each criteria that is given below based on the given prompt.',
      params: {
        fields: [
          {
            required: true,
            min: 1,
            max: 5,
            step: 1,
            use_slider: true,
            type: TextCollectionFieldType.Number,
            field_id: TaxonomyConstant.Eval.StandardFieldId.Accuracy,
            title: 'Accuracy',
            description:
              'The extent to which the information presented is accurate, reliable, and aligns with established facts or evidence.',
            prefix: 'Insufficient',
            mid_label: 'Minimum',
            suffix: 'Good',
            rubric: 'Insert rubric here',
          },
        ],
      },
    },
    {
      id: TaxonomyConstant.SFT.StandardStepId.ModelResponseEditing,
      type: InteractionStepType.ModelResponseEditor,
      title: 'Edit the model response',
      subtitle:
        'Edit the model response to improve its quality. You can edit the response to make it more coherent, accurate, or engaging.',
      params: {},
    },
    {
      id: TaxonomyConstant.SFT.StandardStepId.PostRewriteQuestions,
      type: InteractionStepType.TextCollection,
      title: 'Answer the questions below',
      subtitle: 'Label your response according to the questions below.',
      params: {
        fields: [
          {
            required: true,
            min_characters: 100,
            type: TextCollectionFieldType.Text,
            field_id: TaxonomyConstant.SFT.StandardFieldId.RewriteExplanation,
            title: 'Rewrite explanation',
            description: 'Explain the changes you made in the rewrite.',
          },
        ],
      },
    },
  ],
  taskBuilderFeatures: DEFAULT_TASK_BUILDER_FEATURES_FOR_NEW_PROJECTS,
} as any;

export const PROCESS_SUPERVISION_TASK_PARAMS_TEMPLATE: MultiTurnInteractionConfigurationV2 = {
  minTurns: 1,
  maxTurns: 1,
  history: [],
  before: [],
  after: [TASK_STANDARDIZED_QUALITY_MEASUREMENT_STEP[QualityMeasurementType.Task]],
  turn: [
    {
      id: TaxonomyConstant.ProcessSupervision.StandardStepId.Prompt,
      type: InteractionStepType.PromptInput,
      title: 'Start a Chat',
      subtitle: 'Write a prompt to start a chat session with the model',
      params: {
        needsUserInput: true,
        instructions:
          'You can link a reference doc here/specify what you need: <for example, gdoc link>',
      },
    },
    {
      id: TaxonomyConstant.ProcessSupervision.StandardStepId.ChainOfThought,
      type: InteractionStepType.ChainOfThought,
      title: 'Chain of Thought',
      subtitle: 'Supervise the process of the task.',
      params: {
        chat_model: {
          id: 'Chat Model',
          url: 'https://api.openai.com/v1/chat/completions',
          params: {
            max_tokens: 1024,
            model: 'gpt-3.5-turbo',
          },
        },
        configs: {
          steps: [
            {
              id: 'Thought Correction',
              type: ThoughtStepType.ThoughtCorrection,
              params: {
                maxNudges: 3,
              },
            },
          ],
        },
        chunking_process: {
          stepsChunkingStrategy: StepsChunkingStrategyEnum.ChunkWithDelimiter,
          stepsChunkingParams: {
            systemMessageTemplate: '',
            userMessageTemplate: CHUNK_BY_DELIMITER_DEFAULT_USER_PROMPT,
            delimiter: CHUNK_BY_DELIMITER_DEFAULT_DELIMITER,
            validateChunkedSteps: false,
          },
          stepsRegenStrategy: 'RegenerateWithTemplate',
          stepsRegenParams: {
            template: DEFAULT_STEPS_REGEN_TEMPLATE,
          },
        },
      },
    },
  ],
  taskBuilderFeatures: DEFAULT_TASK_BUILDER_FEATURES_FOR_NEW_PROJECTS,
} as any;
